import { ReactElement, useRef } from 'react';
import { generate } from 'short-uuid';

import { useModalContext } from '../components/Modal/Modal.context';

export interface ModalProps {
	isShown: boolean;
	onClose: () => void;
	/**
	 * Only supplied when the modal was opened with `useModal(render, true)`. Call it to resolve
	 * the promise that `show()` returned; closing without calling it resolves `false`.
	 */
	setConfirmed?: (confirmed: boolean) => void;
}

type RenderDialogFn<Props> = (outgoingProps: ModalProps & Props) => ReactElement;

/**
 * Open a modal imperatively.
 *
 * Pass `awaitConfirmation: true` to have `show()` return a promise that resolves with the user's
 * answer — `true` when the dialog calls `setConfirmed(true)`, `false` when it is closed any other
 * way. Otherwise `show()` returns nothing and the dialog reports back through its own props.
 *
 * @example
 * const [confirmDelete] = useModal<Props>((props) => <DeleteModal {...props} />, true);
 * if (await confirmDelete({ name })) await remove();
 */
export default function useModal<Props = Record<string, unknown>>(
	renderDialog: RenderDialogFn<Props>,
	awaitConfirmation: true
): readonly [(props?: Props) => Promise<boolean | undefined>, () => void];
export default function useModal<Props = Record<string, unknown>>(
	renderDialog: RenderDialogFn<Props>,
	awaitConfirmation?: false
): readonly [(props?: Props) => void, () => void];
export default function useModal<Props = Record<string, unknown>>(
	renderDialog: RenderDialogFn<Props>,
	awaitConfirmation = false
): readonly [(props?: Props) => void | Promise<boolean | undefined>, () => void] {
	const key = useRef(generate()).current;

	const { hideModal, showModal } = useModalContext();

	const hide = () => {
		hideModal(key);
	};

	const show = (props: Props = {} as Props) => {
		// Deliberately not an `async` function: in the common case `show` must stay synchronous, or
		// every `onClick={() => show()}` becomes a promise-returning handler and trips
		// `no-misused-promises` at hundreds of call sites.
		if (!awaitConfirmation) {
			showModal(key, () =>
				renderDialog({
					isShown: true,
					onClose: hide,
					...props,
				})
			);
			return;
		}

		return new Promise<boolean>((resolve) => {
			showModal(key, () =>
				renderDialog({
					isShown: true,
					onClose: () => {
						hide();
						resolve(false);
					},
					setConfirmed: resolve,
					...props,
				})
			);
		});
	};

	return [show, hide] as const;
}
